import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Check, ArrowRight, X, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { products } from '../stripe-config';
import { redirectToCheckout } from '../lib/stripe';
import { toast } from 'sonner';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { BrandGlyph } from '../components/BrandMark';

type TierKey = 'free' | 'starter' | 'pro' | 'team';

interface Tier {
  key: TierKey;
  name: string;
  price: string;
  cadence: string;
  tagline: string;
  description: string;
  features: string[];
  cta: string;
  highlight: boolean;
  badge?: string;
}

const tiers: Tier[] = [
  {
    key: 'free',
    name: 'Free',
    price: '$0',
    cadence: 'forever',
    tagline: 'A real taste, not a trial',
    description: 'Module one of every path so you can feel the four-beat lesson shape before you pay anything.',
    features: [
      'Personal learner account, no card needed',
      'Module one of every path',
      'Hook, Try, Reflect, Apply on every lesson',
      'Reflections saved to your notebook',
      'AI Coach for the basics (light daily limit)',
      'Streaks to build the habit',
    ],
    cta: 'Start free',
    highlight: false,
  },
  {
    key: 'starter',
    name: 'Starter',
    price: '$5',
    cadence: 'per month',
    tagline: 'For one learner getting started',
    description: 'A gentle on-ramp with room to keep going. More AI Coach headroom than Free.',
    features: [
      'Everything in Free',
      'AI Coach with room to keep going',
      'Priority access to new paths as they ship',
      'Cancel any time',
    ],
    cta: 'Start with Starter',
    highlight: false,
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$10',
    cadence: 'per month',
    tagline: 'For consistent learners going deeper',
    description: 'Full paths, all the way through. For learners building real fluency at any age.',
    features: [
      'Everything in Starter',
      'Every module of every path unlocked',
      'Pro-only paths and capstones, with a new drop every month',
      'Unmetered AI Coach for everyday learning',
      'Plain-language reading mode (kids and ESL friendly)',
      'Path completion certificates',
      'Public learner profile and shareable notebook',
      'Export your notebook any time',
    ],
    cta: 'Go Pro',
    highlight: true,
  },
  {
    key: 'team',
    name: 'Team',
    price: '$79',
    cadence: 'per month',
    tagline: 'For small teams learning together',
    description: 'Up to ten people learning side by side, with a shared workspace for notes, prompts, and lessons.',
    features: [
      'Everything in Pro for every member',
      'Up to 10 seats on one subscription',
      'Shared workspace: notes, prompts, lesson tracking',
      'Reusable invite link, rotatable any time',
      'Higher AI Coach limits per member',
      'Owner manages billing and seats',
    ],
    cta: 'Start a team',
    highlight: false,
    badge: 'Best for small teams',
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<TierKey | null>(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  useDocumentMeta({
    title: 'Pricing — Free, $5 Starter, $10 Pro, or $79 Team',
    description:
      'Try a challenge free with no signup. Free accounts get daily challenges and AI grading. Starter $5/mo for more headroom. Pro $10/mo for the full experience. Team $79/mo for up to 10 people.',
    canonical: 'https://promptgpt.ai/pricing',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'What do I actually get for free?', acceptedAnswer: { '@type': 'Answer', text: 'You can complete one full challenge with AI grading without signing up. Free accounts get daily challenges, AI grading, streaks, and a skill score across 10 AI skill areas. No credit card needed.' } },
        { '@type': 'Question', name: 'How does AI grading work?', acceptedAnswer: { '@type': 'Answer', text: 'When you submit a challenge response, an AI grader evaluates it against a challenge-specific rubric. You receive a 0-100 score, written feedback, and skill deltas showing how much each trained skill area improved.' } },
        { '@type': 'Question', name: 'What is the difference between Free, Starter, and Pro?', acceptedAnswer: { '@type': 'Answer', text: 'Free gives you daily challenges and AI grading. Starter ($5/mo) adds more AI Coach headroom. Pro ($10/mo) unlocks the full experience: all challenges, unmetered AI Coach, plain-language mode, certificates, public profile, and notebook export. Team ($79/mo) covers up to 10 people.' } },
        { '@type': 'Question', name: 'Can I cancel anytime?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Cancel from Settings in one click. Your data exports cleanly and your account can be deleted on the way out.' } },
        { '@type': 'Question', name: 'What is the AI Skill Score?', acceptedAnswer: { '@type': 'Answer', text: 'Your AI Skill Score tracks your progress across 10 AI skill areas: Prompting, Context, Research, Writing, Reasoning, Critique, Verification, Automation, Creativity, and Tool Use. Every challenge you complete moves the needle on specific skills.' } },
      ],
    },
  });

  const start = async (tier: Tier) => {
    if (!user) {
      navigate('/login?mode=signup');
      return;
    }
    if (tier.key === 'free') {
      navigate('/learn');
      return;
    }
    if (tier.key === 'team') {
      setTeamModalOpen(true);
      return;
    }
    setLoading(tier.key);
    try {
      const product = products[tier.key];
      await redirectToCheckout(product.priceId, 'subscription');
    } catch (e) {
      toast.error((e as Error).message);
      setLoading(null);
    }
  };

  const startTeam = async () => {
    const name = teamName.trim();
    if (name.length < 2) {
      toast.error('Give your team a name');
      return;
    }
    setLoading('team');
    try {
      await redirectToCheckout(products.team.priceId, 'subscription', {
        team_name: name,
        success_url: `${window.location.origin}/team?welcome=1`,
        cancel_url: `${window.location.origin}/pricing`,
      });
    } catch (e) {
      toast.error((e as Error).message);
      setLoading(null);
    }
  };

  return (
    <div className="container-app py-16 md:py-24">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <div className="inline-flex items-center gap-2 chip mb-5">
          <BrandGlyph size={14} /> Pricing
        </div>
        <h1 className="font-display text-5xl md:text-6xl mb-5">Start free. Upgrade when ready.</h1>
        <p className="text-ink-600 text-lg leading-relaxed">
          A learning layer for ChatGPT, Claude, and Gemini. Free is a real taste of the product —
          not a fourteen-day trial. Move to Starter or Pro only when you want more.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
        {tiers.map((t) => (
          <div
            key={t.name}
            className={`p-7 rounded-2xl flex flex-col ${
              t.highlight ? 'bg-ink-900 text-warm-50 shadow-lift' : 'bg-white border border-ink-100 shadow-soft'
            }`}
          >
            <div className="flex flex-wrap items-baseline gap-2 mb-2">
              <div className={`text-sm font-semibold ${t.highlight ? 'text-accent-300' : 'text-accent-700'}`}>
                {t.name}
              </div>
              {t.highlight && (
                <span className="text-[10px] uppercase tracking-wider bg-accent-500 text-white px-2 py-0.5 rounded-full">
                  Recommended
                </span>
              )}
              {t.badge && (
                <span className="text-[10px] uppercase tracking-wider bg-warm-100 text-ink-700 px-2 py-0.5 rounded-full">
                  {t.badge}
                </span>
              )}
            </div>
            <div className="font-display text-5xl mb-1">{t.price}</div>
            <div className={`text-sm mb-3 ${t.highlight ? 'text-ink-300' : 'text-ink-500'}`}>{t.cadence}</div>
            <p className={`text-sm font-medium mb-2 ${t.highlight ? 'text-accent-300' : 'text-accent-700'}`}>
              {t.tagline}
            </p>
            <p className={`text-sm mb-6 ${t.highlight ? 'text-ink-200' : 'text-ink-600'}`}>{t.description}</p>
            <button
              onClick={() => start(t)}
              disabled={loading === t.key}
              className={
                t.highlight
                  ? 'inline-flex items-center justify-center gap-2 w-full bg-warm-50 text-ink-900 font-medium px-5 py-2.5 rounded-full hover:bg-warm-100 transition disabled:opacity-50'
                  : 'btn-primary w-full justify-center disabled:opacity-50'
              }
            >
              {loading === t.key ? 'Redirecting...' : t.cta} <ArrowRight className="w-4 h-4" />
            </button>
            <ul className="space-y-2.5 mt-7">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check
                    className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      t.highlight ? 'text-accent-300' : 'text-accent-600'
                    }`}
                  />
                  <span className={`text-sm ${t.highlight ? 'text-ink-100' : 'text-ink-700'}`}>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto mt-20">
        <h2 className="font-display text-3xl mb-8 text-center">Questions people ask</h2>
        <div className="space-y-4">
          {[
            {
              q: 'What do I actually get for free?',
              a: 'Module one of every path, the four-beat lesson shape on every lesson, your reflections saved to a notebook, streaks, and the AI Coach with a five-conversations-a-day limit. No credit card. No fourteen-day timer. Stay on Free as long as you like — upgrade only when you want more.',
            },
            {
              q: 'Why not just use ChatGPT directly?',
              a: 'ChatGPT is a brilliant answer machine — but a blank chat box is paralysis when you do not yet know the right question. PromptGPT wraps the same models in a curriculum: a sequenced path, a four-beat lesson shape, and a reflection step that moves a skill from a page into your head. If you already know what to ask, go straight to ChatGPT. If you are trying to learn, the structure is the point.',
            },
            {
              q: 'What is the difference between Free, Starter, and Pro?',
              a: 'Free and Starter both give you module one of every path. Starter raises the AI Coach headroom so light daily caps stop getting in your way. Pro unlocks every module on every path, opens the Pro-only paths (Work, Job Seekers, and more on the way), adds plain-language reading mode, completion certificates, a public learner profile, full notebook export, and unmetered AI Coach. Stay on Free until the curriculum or the headroom push you to upgrade.',
            },
            {
              q: 'How often does the curriculum grow?',
              a: 'A new Pro path, capstone, or workshop ships roughly every month. Subscribers pick them up automatically — no extra charges, no add-on tiers. The library is built to keep going, not to be finished and cancelled.',
            },
            {
              q: 'How are lessons structured?',
              a: 'Every lesson follows the same shape: a Hook to get you thinking, a Try section to practice, a Reflect prompt to notice what changed, and an Apply prompt to take it somewhere new. Your reflections are saved to your notebook so you can come back to them.',
            },
            {
              q: 'Does this work for kids and adult beginners alike?',
              a: 'Yes. The same lessons adapt to a "plain language" reading mode with shorter sentences and friendlier examples. Pick it during onboarding or switch any time in settings — the structure stays identical, only the wording changes.',
            },
            {
              q: 'How long should a session be?',
              a: 'Five to fifteen minutes per lesson is plenty. Each lesson is sized to fit one sitting, so the rhythm stays a healthy habit rather than a binge.',
            },
            {
              q: 'Can I cancel anytime?',
              a: 'Yes. Cancel from Settings in one click. Your reflections export cleanly and profiles can be deleted on the way out.',
            },
            {
              q: 'Can I upgrade or downgrade later?',
              a: 'Yes. Switch between Starter and Pro any time — Stripe handles the prorated charge.',
            },
          ].map((f) => (
            <details key={f.q} className="group card p-5 cursor-pointer">
              <summary className="flex items-center justify-between font-medium list-none">
                {f.q}
                <span className="text-ink-400 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="text-ink-600 text-sm mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      {teamModalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 px-4">
          <div className="bg-white rounded-2xl shadow-lift w-full max-w-md p-6 relative">
            <button
              onClick={() => setTeamModalOpen(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-warm-100"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-ink-500" />
            </button>
            <div className="w-10 h-10 rounded-full bg-accent-100 grid place-items-center mb-4">
              <Users className="w-5 h-5 text-accent-700" />
            </div>
            <h3 className="font-display text-2xl mb-1">Name your team</h3>
            <p className="text-sm text-ink-600 mb-5">
              You can change this any time from your team settings.
            </p>
            <input
              autoFocus
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Acme Learning Team"
              className="w-full px-4 py-3 rounded-xl border border-ink-200 focus:border-ink-400 focus:outline-none mb-4"
              onKeyDown={(e) => {
                if (e.key === 'Enter') startTeam();
              }}
            />
            <button
              onClick={startTeam}
              disabled={loading === 'team'}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading === 'team' ? 'Redirecting...' : 'Continue to checkout'}
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] text-ink-500 mt-3 text-center">
              $79 a month, up to 10 seats. Cancel any time.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
